'use client';

import { useState } from 'react';
import type { ReactNode } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import ContactModal from './ContactModal';
import BrandLogo from '@/components/brand/BrandLogo';

const FooterSection = () => {
    const [currentYear] = useState(() => new Date().getFullYear());
    const [isContactOpen, setIsContactOpen] = useState(false);

    return (
        <footer className="py-16 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(212,175,55,0.08),_transparent_32%),linear-gradient(180deg,rgba(9,12,20,0.05),rgba(9,12,20,0.28))] pointer-events-none"></div>

            <ContactModal
                isOpen={isContactOpen}
                onClose={() => setIsContactOpen(false)}
            />

            <div className="container mx-auto px-5 relative z-10">
                <div className="max-w-7xl mx-auto card-surface manuscript-frame rounded-[2rem] px-8 py-10 md:px-12 md:py-12">
                    <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
                        <div className="md:col-span-1">
                            <div className="mb-6 text-foreground">
                                <BrandLogo size="md" textClassName="tracking-tight" />
                            </div>
                            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                                A personal spiritual dashboard for daily aura, horoscope, charts, numerology, palm reading, tarot, dreams, guidance, saved readings, and community.
                            </p>
                            <div className="flex items-center space-x-4">
                                <SocialLink href="https://twitter.com/your_aura_life" icon="AtSymbolIcon" label="Twitter" />
                                <SocialLink href="https://www.instagram.com/youraura.life/" icon="CameraIcon" label="Instagram" />
                                <SocialLink href="https://www.facebook.com/youraura.life" icon="UserGroupIcon" label="Facebook" />
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold text-[#f5e6c8] mb-6 uppercase tracking-[0.24em] text-xs">Product</h4>
                            <ul className="space-y-4">
                                <FooterLink href="/about">About YourAura</FooterLink>
                                <FooterLink href="#how-it-works">How It Works</FooterLink>
                                <FooterLink href="#start">Start Free</FooterLink>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-bold text-[#f5e6c8] mb-6 uppercase tracking-[0.24em] text-xs">Resources</h4>
                            <ul className="space-y-4">
                                <FooterLink href="#faq">FAQ</FooterLink>
                                <FooterLink href="/blog">Blog</FooterLink>
                                <FooterLink href="/astrology">Astrology Hub</FooterLink>
                                <FooterLink href="/numerology">Numerology Hub</FooterLink>
                                <FooterLink href="/palmistry">Palmistry Hub</FooterLink>
                                <FooterLink href="/blog/authors">Authors</FooterLink>
                                <FooterLink href="/press">Press &amp; Media</FooterLink>
                                <FooterLink href="/youraura-vs-generic-horoscope-apps">YourAura vs Horoscope Apps</FooterLink>
                                <FooterLink href="/methodology">Methodology</FooterLink>
                                <FooterLink href="/editorial-policy">Editorial Policy</FooterLink>
                                <li>
                                    <button onClick={() => setIsContactOpen(true)} className="text-sm text-muted-foreground hover:text-accent transition-colors duration-200 block hover:translate-x-1 transform text-left w-full">
                                        Support Center
                                    </button>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-bold text-[#f5e6c8] mb-6 uppercase tracking-[0.24em] text-xs">Free Tools</h4>
                            <ul className="space-y-4">
                                <FooterLink href="/free-birth-chart-reading">Birth Chart Reading</FooterLink>
                                <FooterLink href="/moon-sign-calculator">Moon Sign Calculator</FooterLink>
                                <FooterLink href="/life-path-number-calculator">Life Path Calculator</FooterLink>
                                <FooterLink href="/ai-palm-reading">AI Palm Reading</FooterLink>
                                <FooterLink href="/personalized-daily-horoscope">Daily Horoscope</FooterLink>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-bold text-[#f5e6c8] mb-6 uppercase tracking-[0.24em] text-xs">Legal</h4>
                            <ul className="space-y-4">
                                <FooterLink href="/privacy">Privacy Policy</FooterLink>
                                <FooterLink href="/terms">Terms of Service</FooterLink>
                                <FooterLink href="/methodology">Methodology</FooterLink>
                                <FooterLink href="/editorial-policy">Editorial Policy</FooterLink>
                                <FooterLink href="/about">Company Overview</FooterLink>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-[#e8d7b0]/10 pt-8 mt-8">
                        <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0 text-sm text-muted-foreground">
                            <p>&copy; {currentYear} YourAura. All rights reserved.</p>
                            <div className="flex items-center space-x-6">
                                <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
                                <Link href="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
                                <button onClick={() => setIsContactOpen(true)} className="hover:text-foreground transition-colors">Contact</button>
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground/50 text-center mt-8 max-w-2xl mx-auto">
                            Not medical or financial advice. Designed for reflection and clarity.
                            <br />
                            Built for practical self-reflection, not fixed-fate promises.
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
};

const SocialLink = ({ href, icon, label }: { href: string; icon: string; label: string }) => (
    <a
        href={href}
        rel="noopener noreferrer"
        target="_blank"
        className="card-quiet card-interactive flex h-10 w-10 items-center justify-center text-[var(--manuscript-ink)] transition-colors"
        aria-label={label}
    >
        <Icon name={icon} size={18} className="text-current transition-colors" />
    </a>
);

const FooterLink = ({ href, children }: { href: string; children: ReactNode }) => (
    <li>
        <Link href={href} className="text-sm text-muted-foreground hover:text-accent transition-colors duration-200 block hover:translate-x-1 transform">
            {children}
        </Link>
    </li>
);

export default FooterSection;
